'use client';

import { useTranslations } from 'next-intl';
import { StagePill } from '@/features/registration';
import type { RegistrationStage } from '@/features/registration';

/**
 * Registration stages that mean a container has finished the OCR stage and
 * moved into the OCR-QA tiers (QA1 → QA2 → QA3). Once a container is here, the
 * "move to repair" action is hidden and the card / compact views show the QA
 * tier instead of the generic "completed" badge. The table view does not — it
 * has a dedicated `review_status` column for that.
 */
export const OCR_QA_STAGES = ['qa1_ocr', 'qa2_ocr', 'qa3_ocr'] as const;

type OcrQaStage = (typeof OCR_QA_STAGES)[number];

const OCR_QA_STAGE_LABEL_KEYS: Record<OcrQaStage, string> = {
  qa1_ocr: 'stageQa1Ocr',
  qa2_ocr: 'stageQa2Ocr',
  qa3_ocr: 'stageQa3Ocr'
};

/** True once the container has left the OCR stage and entered an OCR-QA tier. */
export function isInOcrQa(
  stage: RegistrationStage | null | undefined
): stage is OcrQaStage {
  return stage != null && (OCR_QA_STAGES as readonly string[]).includes(stage);
}

/** Indigo stage pill naming which OCR-QA tier a container currently sits in. */
export function OcrQaStagePill({ stage }: { stage: OcrQaStage }) {
  const tReg = useTranslations('registration');
  return (
    <StagePill
      stage={stage}
      label={tReg(OCR_QA_STAGE_LABEL_KEYS[stage] as never)}
    />
  );
}
